import { useState } from "react";
import { toast } from "sonner";
import { Barcode, Keyboard, Warning, CheckCircle, Trash } from "@phosphor-icons/react";
import { MaterialIcon } from "../../lib/materialIcons";
import { ScannerDialog } from "./ScannerDialog";
import { Button } from "../ui/button";

const SerialLine = ({ line, values, onChange, allValues }) => {
  const [scan, setScan] = useState(false);
  const qty = Math.floor(line.qty);
  const filled = values.filter((v) => v.trim()).length;
  const done = filled >= qty;

  const set = (i, v) => { const n = [...values]; n[i] = v.toUpperCase(); onChange(n); };
  const addScanned = (text) => {
    const v = text.toUpperCase();
    if (allValues.includes(v)) { toast.error(`${v} is already entered`); return true; }
    const n = [...values];
    const i = n.findIndex((x) => !x.trim());
    if (i === -1) { toast.warning("All serials are filled"); return false; }
    n[i] = v; onChange(n);
    toast.success(`Serial ${i + 1}/${qty}: ${v}`);
    return n.some((x) => !x.trim());
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`serial-line-${line.key}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <MaterialIcon iconKey={line.icon_key} size={38} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{line.label}</div>
          <div className={`text-[11px] font-mono font-bold ${done ? "text-emerald-600" : "text-amber-600"}`} data-testid={`serial-count-${line.key}`}>
            {filled} / {qty} serials {done ? "✓" : ""}
          </div>
        </div>
        <Button type="button" size="sm" data-testid={`serial-scan-btn-${line.key}`} onClick={() => setScan(true)}
          className="h-9 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs px-3">
          <Barcode size={15} className="mr-1" /> Scan
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
        {values.map((v, i) => (
          <div key={i} className="relative flex items-center">
            <span className="absolute left-3 text-[10px] font-mono font-bold text-slate-400 w-5">{String(i + 1).padStart(2, "0")}</span>
            <input data-testid={`serial-input-${line.key}-${i}`} value={v} onChange={(e) => set(i, e.target.value)}
              placeholder="Type / scan serial" autoCapitalize="characters"
              className={`w-full h-10 pl-10 pr-9 rounded-lg border text-sm font-mono tracking-wider bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 ${v.trim() ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} />
            {v && <button type="button" className="absolute right-2 p-1 text-slate-400 hover:text-rose-600" onClick={() => set(i, "")} data-testid={`serial-clear-${line.key}-${i}`}><Trash size={14} /></button>}
          </div>
        ))}
      </div>
      <ScannerDialog open={scan} onClose={() => setScan(false)} continuous onResult={addScanned}
        title={`Scan — ${line.label}`} hint="Scan each piece one by one. Each scan fills the next slot." />
    </div>
  );
};

export function SerialStep({ lines, serials, setSerials }) {
  const all = Object.values(serials).flat().filter((v) => v.trim());
  const short = lines.filter((l) => (serials[l.key] || []).filter((v) => v.trim()).length < Math.floor(l.qty));
  if (!lines.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-500" data-testid="serial-none">
        <CheckCircle size={28} className="mx-auto mb-2 text-emerald-500" /> No panels, inverters or batteries in this dispatch — serials are not needed.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-slate-500"><Keyboard size={14} /> Scan with camera, scan from a photo, or type manually</div>
      {short.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900 flex gap-2" data-testid="serial-warning">
          <Warning size={18} weight="fill" className="text-amber-500 shrink-0" />
          <div><b>Missing serial numbers:</b> {short.map((l) => `${l.label} (${(serials[l.key] || []).filter((v) => v.trim()).length}/${Math.floor(l.qty)})`).join(", ")}</div>
        </div>
      )}
      {lines.map((l) => (
        <SerialLine key={l.key} line={l} values={serials[l.key] || []} allValues={all}
          onChange={(v) => setSerials((s) => ({ ...s, [l.key]: v }))} />
      ))}
    </div>
  );
}
